/**
 * AlChatPanel - Route-aware AI chat panel for the Al widget
 *
 * Adapts suggested prompts and placeholder text based on current module.
 * Passes route context to Cloud Function so AI knows which screen the user is on.
 * Conversation persists across navigation — route changes don't reset the chat.
 *
 * @example
 * ```tsx
 * <AlChatPanel
 *   routeContext={{ currentRoute: '/crm/leads/123', previousRoute: '/crm/leads', currentModule: 'sales' }}
 *   onConversationCreated={(id) => setConversationId(id)}
 * />
 * ```
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { safeFormatDate } from '../../lib/utils/safeFormatDate';
import { Timestamp } from 'firebase/firestore';
import {
  Send,
  Loader2,
  Bot,
  Sparkles,
  ExternalLink,
  CheckCircle2,
  ThumbsUp,
  ThumbsDown,
  Copy,
  Check,
} from 'lucide-react';
import { useConversationMessages, useSendMessage, useExecuteAction } from '../../hooks/crm/useAISalesChat';
import { useSubmitAIFeedback, useUserAIFeedback } from '../../hooks/ai-systems/useAIFeedback';
import type { ConversationMessage, MessageAction, SuggestedPrompt } from '../../types/ai-sales-chat';
import { ASSISTANT_NAME } from './assistantName';
import { compareMessagesByTime } from './alChatMessageOrder';

// ============================================================================
// TYPES
// ============================================================================

interface RouteContext {
  currentRoute: string;
  previousRoute?: string;
  currentModule: string;
}

interface AlChatPanelProps {
  conversationId?: string;
  onConversationCreated?: (id: string) => void;
  routeContext: RouteContext;
  className?: string;
}

// ============================================================================
// MODULE-SPECIFIC SUGGESTED PROMPTS
// ============================================================================

const PROMPTS_BY_MODULE: Record<string, SuggestedPrompt[]> = {
  sales: [
    { label: 'Research this lead', prompt: 'Research this lead and provide key insights', icon: 'search' },
    { label: 'Draft outreach email', prompt: 'Draft a professional outreach email for this lead', icon: 'mail' },
    { label: 'Prioritize my leads', prompt: 'Which leads in my pipeline should I focus on today?', icon: 'list' },
    { label: 'Check activity history', prompt: 'What is the recent activity history for this lead?', icon: 'clock' },
  ],
  support: [
    { label: 'Summarize this case', prompt: 'Summarize the current support case and suggest next steps', icon: 'search' },
    { label: 'Draft response', prompt: 'Draft a professional response to the customer', icon: 'mail' },
    { label: 'Find related cases', prompt: 'Are there related support cases or known issues?', icon: 'list' },
    { label: 'Escalation check', prompt: 'Should this case be escalated? What is the SLA status?', icon: 'alert' },
  ],
  projects: [
    { label: 'Project status', prompt: 'What is the current status of this project?', icon: 'search' },
    { label: 'Equipment details', prompt: 'What equipment is associated with this project?', icon: 'list' },
    { label: 'Timeline check', prompt: 'Are there any overdue milestones or permits?', icon: 'clock' },
    { label: 'Contact history', prompt: 'What is the communication history for this project?', icon: 'users' },
  ],
  quoting: [
    { label: 'Quote summary', prompt: 'Summarize this quote — items, totals, and status', icon: 'search' },
    { label: 'Compare pricing', prompt: 'How does this quote compare to similar recent quotes?', icon: 'list' },
    { label: 'Customer history', prompt: 'What is the quoting history with this customer?', icon: 'clock' },
    { label: 'Follow-up draft', prompt: 'Draft a follow-up email about this quote', icon: 'mail' },
  ],
  billing: [
    { label: 'Payment status', prompt: 'What is the payment status for this account?', icon: 'search' },
    { label: 'Outstanding invoices', prompt: 'List outstanding invoices and aging for this customer', icon: 'list' },
    { label: 'Collection strategy', prompt: 'Suggest a collection strategy for overdue accounts', icon: 'alert' },
    { label: 'Payment reminder', prompt: 'Draft a professional payment reminder email', icon: 'mail' },
  ],
  assets: [
    { label: 'Asset status', prompt: 'What is the current status and location of this asset?', icon: 'search' },
    { label: 'Maintenance history', prompt: 'What is the maintenance and service history?', icon: 'clock' },
    { label: 'Compliance check', prompt: 'Are there any compliance or inspection items due?', icon: 'alert' },
    { label: 'Fleet overview', prompt: 'Give me an overview of the fleet status', icon: 'list' },
  ],
  insurance: [
    { label: 'Expiring COIs', prompt: '#david-ai Which vendor COIs expire in the next 30 days?', icon: 'alert' },
    { label: 'Our coverage', prompt: '#david-ai What are our current GL limits and carrier?', icon: 'search' },
    { label: 'Request a COI', prompt: '#david-ai Request a COI from [vendor name] naming us as additional insured', icon: 'mail' },
    { label: 'Open claims', prompt: '#david-ai What claims are currently open?', icon: 'list' },
  ],
  marketing: [
    { label: 'Content strategy', prompt: '#marketing-agent What is our content strategy this week?', icon: 'search' },
    { label: 'Competitor activity', prompt: '#marketing-agent What competitor changes happened recently?', icon: 'list' },
    { label: 'Draft status', prompt: '#marketing-agent How many posts are in draft vs review vs published?', icon: 'clock' },
    { label: 'SEO gaps', prompt: '#marketing-agent What content gaps should we target next?', icon: 'alert' },
  ],
  general: [
    { label: 'What can you do?', prompt: 'What can you help me with?', icon: 'search' },
    { label: 'Today\'s priorities', prompt: 'What should I focus on today across all modules?', icon: 'list' },
    { label: 'Recent activity', prompt: 'Summarize recent activity across the platform', icon: 'clock' },
    { label: 'Search everything', prompt: 'Search across all data for...', icon: 'search' },
  ],
};

const PLACEHOLDER_BY_MODULE: Record<string, string> = {
  sales: `Ask ${ASSISTANT_NAME} about your leads, pipeline, or outreach...`,
  support: `Ask ${ASSISTANT_NAME} about cases, customers, or escalations...`,
  projects: `Ask ${ASSISTANT_NAME} about project status, equipment, or permits...`,
  quoting: `Ask ${ASSISTANT_NAME} about quotes, pricing, or customers...`,
  billing: `Ask ${ASSISTANT_NAME} about invoices, payments, or collections...`,
  assets: `Ask ${ASSISTANT_NAME} about fleet, maintenance, or compliance...`,
  insurance: 'Ask David about policies, COIs, claims, or coverage...',
  marketing: 'Ask Mary about content, competitors, or SEO...',
  general: `Ask ${ASSISTANT_NAME} anything...`,
};

// ============================================================================
// COMPONENT
// ============================================================================

export function AlChatPanel({
  conversationId: externalConversationId,
  onConversationCreated,
  routeContext,
  className = '',
}: AlChatPanelProps) {
  const navigate = useNavigate();
  const [activeConversationId, setActiveConversationId] = useState<string | undefined>(externalConversationId);
  const [pendingMessages, setPendingMessages] = useState<ConversationMessage[]>([]);

  const { data: serverMessages = [], isLoading: messagesLoading } = useConversationMessages(activeConversationId);
  const { mutate: sendMessage, isPending: isSending } = useSendMessage();
  const { mutate: executeAction, isPending: isExecutingAction } = useExecuteAction();

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Sync external conversationId
  useEffect(() => {
    if (externalConversationId !== undefined) setActiveConversationId(externalConversationId);
  }, [externalConversationId]);

  // Sort ascending by createdAt; for same timestamp user message comes before assistant
  const rawMessages = serverMessages.length > 0 ? serverMessages : pendingMessages;
  const messages = [...rawMessages].sort(compareMessagesByTime);

  const suggestedPrompts = useMemo(
    () => PROMPTS_BY_MODULE[routeContext.currentModule] || PROMPTS_BY_MODULE.general,
    [routeContext.currentModule],
  );

  const placeholder = PLACEHOLDER_BY_MODULE[routeContext.currentModule] || PLACEHOLDER_BY_MODULE.general;

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-focus
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = () => {
    if (!inputValue.trim() || isSending) return;

    const messageText = inputValue.trim();

    const optimisticMsg: ConversationMessage = {
      messageId: `pending-${Date.now()}`,
      conversationId: activeConversationId || '',
      role: 'user' as const,
      content: messageText,
      createdAt: Timestamp.now(),
    };
    setPendingMessages((prev) => [...prev, optimisticMsg]);

    sendMessage(
      {
        conversationId: activeConversationId,
        message: messageText,
        context: {
          // Pass route context alongside any entity context
          currentRoute: routeContext.currentRoute,
          previousRoute: routeContext.previousRoute,
          currentModule: routeContext.currentModule,
        },
      },
      {
        onSuccess: (data) => {
          if (data.conversationId && !activeConversationId) {
            setActiveConversationId(data.conversationId);
            onConversationCreated?.(data.conversationId);
          }
          // Replace pending messages with real messages from backend
          // This avoids a flash of empty state while React Query refetches
          if (data.userMessage && data.assistantMessage) {
            setPendingMessages([data.userMessage, data.assistantMessage]);
          } else {
            setPendingMessages([]);
          }

          // Handle navigation from skill results (response-only, not persisted)
          if (data.navigateTo) {
            setTimeout(() => {
              const target = data.navigateTo!;
              // External URIs (tel:, mailto:, https:) open in new tab/handler
              if (target.startsWith('tel:') || target.startsWith('mailto:') || target.startsWith('http')) {
                window.open(target, '_blank');
              } else {
                // Internal routes use React Router
                navigate(target);
              }
            }, 800);
          }
        },
        onError: () => {
          setPendingMessages((prev) => prev.filter((m) => m.messageId !== optimisticMsg.messageId));
        },
      },
    );

    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedPrompt = (prompt: string) => {
    setInputValue(prompt);
    inputRef.current?.focus();
  };

  const handleExecuteAction = (messageId: string, actionIndex: number) => {
    if (!activeConversationId) return;
    executeAction(
      { conversationId: activeConversationId, messageId, actionIndex },
    );
  };

  const showEmptyState = !messagesLoading && messages.length === 0;
  const showTypingIndicator = isSending;

  return (
    <div className={`flex flex-col h-full bg-white ${className}`}>
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {/* Empty State */}
        {showEmptyState && (
          <div className="flex flex-col items-center justify-center h-full text-center px-3">
            <div className="p-3 bg-indigo-100 rounded-full mb-3">
              <Bot className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-base font-semibold text-slate-900 mb-1">
              Hey, I'm {ASSISTANT_NAME}
            </h3>
            <p className="text-xs text-slate-500 mb-4 max-w-[280px]">
              Your AI assistant. I can help with research, drafting, data lookups, and more across any module.
            </p>

            {/* Suggested Prompts */}
            <div className="grid grid-cols-1 gap-2 w-full">
              {suggestedPrompts.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestedPrompt(prompt.prompt)}
                  className="flex items-center gap-2 p-2.5 text-left border border-slate-300 rounded-xl shadow-sm hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                >
                  <Sparkles className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                  <span className="text-xs text-slate-700">{prompt.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Message List */}
        {messages.map((message) => (
          <MessageBubble
            key={message.messageId}
            message={message}
            onExecuteAction={handleExecuteAction}
            isExecutingAction={isExecutingAction}
          />
        ))}

        {/* Typing Indicator */}
        {showTypingIndicator && (
          <div className="flex items-start gap-2">
            <div className="p-1.5 bg-indigo-100 rounded-full flex-shrink-0">
              <Bot className="w-3.5 h-3.5 text-indigo-600" />
            </div>
            <div className="flex-1 bg-slate-100 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />
                <span className="text-xs text-slate-500">{ASSISTANT_NAME} is thinking...</span>
              </div>
            </div>
          </div>
        )}

        {/* Loading Skeleton */}
        {messagesLoading && (
          <div className="space-y-3">
            <MessageSkeleton role="user" />
            <MessageSkeleton role="assistant" />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-slate-200 p-3 bg-slate-50">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={1}
              className="w-full px-3 py-2 border border-slate-300 rounded-xl shadow-sm text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              style={{ minHeight: '40px', maxHeight: '100px' }}
            />
          </div>
          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || isSending}
            className="p-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MESSAGE BUBBLE
// ============================================================================

interface MessageBubbleProps {
  message: ConversationMessage;
  onExecuteAction: (messageId: string, actionIndex: number) => void;
  isExecutingAction: boolean;
}

/** Normalize AI response content: collapse 3+ newlines to 2, trim edges */
function normalizeContent(text: string): string {
  if (!text) return '';
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

/** Render inline markdown: **bold** and ## headings */
function renderInlineMarkdown(line: string, key: number) {
  // Heading: ## or ###
  if (/^#{1,3}\s/.test(line)) {
    const text = line.replace(/^#+\s*/, '');
    return <p key={key} className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mt-2 mb-0.5">{text}</p>;
  }
  // Normal line with possible **bold** segments
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return (
    <span key={key}>
      {parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={i}>{part.slice(2, -2)}</strong>
          : part
      )}
    </span>
  );
}

function MessageContent({ content }: { content: string }) {
  const normalized = normalizeContent(content);
  const paragraphs = normalized.split('\n\n');
  return (
    <div className="space-y-1">
      {paragraphs.map((para, pi) => {
        const lines = para.split('\n');
        return (
          <p key={pi} className="text-xs leading-relaxed select-text">
            {lines.map((line, li) => (
              <React.Fragment key={li}>
                {li > 0 && <br />}
                {renderInlineMarkdown(line, li)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

function MessageBubble({ message, onExecuteAction, isExecutingAction }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const { safeWriteClipboard } = await import('../../lib/utils/clipboard');
    const ok = await safeWriteClipboard(message.content);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className={`flex items-start gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Only show avatar for assistant messages */}
      {!isUser && (
        <div className="p-1 rounded-full flex-shrink-0 bg-indigo-100 mt-0.5">
          <Bot className="w-3 h-3 text-indigo-600" />
        </div>
      )}

      <div className={`flex-1 max-w-[85%] ${isUser ? 'items-end' : ''}`}>
        <div
          className={`group relative rounded-lg px-2.5 py-1.5 ${
            isUser ? 'bg-blue-600 text-white ml-auto' : 'bg-slate-100 text-slate-900'
          }`}
        >
          {isUser
            ? <p className="text-xs leading-relaxed select-text">{message.content}</p>
            : <MessageContent content={message.content} />
          }
          <p className={`text-[9px] mt-1 ${isUser ? 'text-blue-200' : 'text-slate-400'}`}>
            {safeFormatDate(message.createdAt, 'h:mm a')}
          </p>
          {!isUser && (
            <button
              onClick={handleCopy}
              className="absolute top-1.5 right-1.5 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity bg-slate-200 hover:bg-slate-300 text-slate-500"
              title="Copy reply"
            >
              {copied ? <Check className="w-2.5 h-2.5 text-green-600" /> : <Copy className="w-2.5 h-2.5" />}
            </button>
          )}
        </div>

        {!isUser && message.metadata?.responseConfidence === 'medium' && (
          <span className="text-xs text-slate-400 mt-1 block">Based on available records</span>
        )}
        {!isUser && message.metadata?.responseConfidence === 'low' && (
          <span className="text-xs text-amber-500 mt-1 block">Low confidence — try rephrasing or check the module directly</span>
        )}

        {!isUser && message.actions && message.actions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {message.actions.map((action, index) => (
              <ActionButton
                key={index}
                action={action}
                onClick={() => onExecuteAction(message.messageId, index)}
                disabled={isExecutingAction || !!action.executed}
              />
            ))}
          </div>
        )}

        {!isUser && <MessageFeedback messageId={message.messageId} />}
      </div>
    </div>
  );
}

// ============================================================================
// MESSAGE FEEDBACK
// ============================================================================

function MessageFeedback({ messageId }: { messageId: string }) {
  const { data: existing } = useUserAIFeedback(messageId);
  const { mutate: submitFeedback, isPending } = useSubmitAIFeedback();

  const handleFeedback = (feedbackType: 'helpful' | 'not_helpful') => {
    if (existing?.hasFeedback || isPending) return;
    submitFeedback({ aiOutputId: messageId, feedbackType });
  };

  if (existing?.hasFeedback) {
    return (
      <div className="mt-1 flex items-center gap-1 text-[10px] text-slate-400">
        {existing.feedbackType === 'helpful' ? (
          <><ThumbsUp className="w-2.5 h-2.5 text-green-500" /> Helpful</>
        ) : (
          <><ThumbsDown className="w-2.5 h-2.5 text-red-400" /> Not helpful</>
        )}
      </div>
    );
  }

  return (
    <div className="mt-1 flex items-center gap-1.5">
      <button
        onClick={() => handleFeedback('helpful')}
        disabled={isPending}
        className="p-0.5 text-slate-300 hover:text-green-500 transition-colors disabled:opacity-50"
        title="Helpful"
      >
        <ThumbsUp className="w-3 h-3" />
      </button>
      <button
        onClick={() => handleFeedback('not_helpful')}
        disabled={isPending}
        className="p-0.5 text-slate-300 hover:text-red-400 transition-colors disabled:opacity-50"
        title="Not helpful"
      >
        <ThumbsDown className="w-3 h-3" />
      </button>
    </div>
  );
}

// ============================================================================
// ACTION BUTTON
// ============================================================================

function ActionButton({ action, onClick, disabled }: { action: MessageAction; onClick: () => void; disabled: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors ${
        action.executed
          ? 'bg-green-50 text-green-700 border-green-200 cursor-not-allowed'
          : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed'
      }`}
    >
      {action.executed ? (
        <CheckCircle2 className="w-3 h-3" />
      ) : (
        <ExternalLink className="w-3 h-3" />
      )}
      <span>{action.label}</span>
    </button>
  );
}

// ============================================================================
// SKELETON
// ============================================================================

function MessageSkeleton({ role }: { role: 'user' | 'assistant' }) {
  const isUser = role === 'user';
  return (
    <div className={`flex items-start gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
      {!isUser && <div className="w-5 h-5 rounded-full animate-pulse flex-shrink-0 bg-indigo-200 mt-0.5" />}
      <div className={`flex-1 max-w-[80%] ${isUser ? 'items-end' : ''}`}>
        <div className={`rounded-lg p-3 space-y-1.5 animate-pulse ${isUser ? 'bg-blue-200 ml-auto' : 'bg-slate-200'}`}>
          <div className="h-2.5 bg-slate-300 rounded w-3/4" />
          <div className="h-2.5 bg-slate-300 rounded w-1/2" />
        </div>
      </div>
    </div>
  );
}

export default AlChatPanel;
